import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { PasswordValidation } from '@/components/ui/PasswordValidation'
import { validatePassword } from '@/lib/utils'
import { toast } from 'sonner'
import { AuthLayout } from '@/layout/AuthLayout'

export function SignUp() {
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { signUp } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (password !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    // Validate password requirements
    const passwordValidation = validatePassword(password)
    if (!passwordValidation.isValid) {
      toast.error('Password does not meet all requirements')
      return
    }

    setIsLoading(true)

    try {
      await signUp(username, password, email, firstName, lastName)
      toast.success(
        'Account created! Please check your email for verification code.',
      )
      navigate('/auth/confirm-signup', { state: { username } })
    } catch (error) {
      toast.error('Failed to create account. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthLayout>
      <Card className="border-0 shadow-none">
        <CardHeader className="pb-4">
          <CardTitle className="text-2xl py-4">Create Account</CardTitle>
          <CardDescription className="text-xs">
            Enter your details to create a new account
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Input
                id="username"
                type="text"
                placeholder="Username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                required
                className="rounded-xl shadow-xl"
              />
            </div>
            <div className="space-y-2">
              <Input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                placeholder="Email"
                className="rounded-xl shadow-xl"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  required
                  placeholder="First Name"
                  className="rounded-xl shadow-xl"
                />
              </div>
              <div className="space-y-2">
                <Input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  required
                  placeholder="Last Name"
                  className="rounded-xl shadow-xl"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Input
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                placeholder="Password"
                className="rounded-xl shadow-xl"
              />
              <PasswordValidation password={password} />
            </div>
            <div className="space-y-2">
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm Password"
                className="rounded-xl shadow-xl"
              />
            </div>
            <Button
              type="submit"
              className="w-full rounded-xl shadow-xl mt-4"
              disabled={isLoading}
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex flex-col space-y-2 pt-4">
          <div className="text-xs text-slate-600 dark:text-slate-400">
            Already have an account?{' '}
            <Link
              to="/auth/signin"
              className="text-xs text-blue-700 hover:underline dark:text-blue-400"
            >
              Sign in
            </Link>
          </div>
        </CardFooter>
      </Card>
    </AuthLayout>
  )
}
